import type { CSSProperties, FC, ReactNode } from 'react'

import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'

export type LegalSection = {
    id: string
    title: string
    body: ReactNode
}

interface LegalPageProps {
    eyebrow: string
    title: string
    updated: string
    intro: ReactNode
    sections: Array<LegalSection>
}

const delay = (ms: number): CSSProperties => ({ '--rise-delay': `${ms}ms` } as CSSProperties)

export const LegalPage: FC<LegalPageProps> = ({ eyebrow, title, updated, intro, sections }): ReactNode => {
    return (
        <article className='bg-cream'>
            <Container className='pb-24 pt-36 md:pb-32 md:pt-44'>
                <header className='max-w-3xl'>
                    <div className='animate-rise' style={delay(40)}>
                        <Eyebrow>{eyebrow}</Eyebrow>
                    </div>

                    <h1 className='animate-rise mt-7 font-heading text-display-lg font-medium text-burgundy' style={delay(120)}>
                        {title}
                    </h1>

                    <p className='animate-rise mt-5 text-sm text-stone' style={delay(180)}>Last updated {updated}</p>

                    <div className='animate-rise mt-8 text-lg leading-relaxed text-stone-dark' style={delay(240)}>{intro}</div>
                </header>

                <div className='mt-16 grid gap-12 border-t border-burgundy/10 pt-12 lg:grid-cols-12 lg:gap-10'>
                    <nav aria-label='On this page' className='lg:col-span-3'>
                        <div className='lg:sticky lg:top-28'>
                            <p className='font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-stone'>On this page</p>

                            <ol className='mt-4 space-y-2'>
                                {sections.map((section, index) => (
                                    <li key={section.id}>
                                        <a href={`#${section.id}`} className='flex gap-3 text-sm text-stone-dark transition-colors hover:text-burgundy'>
                                            <span className='font-mono text-xs text-stone'>{String(index + 1).padStart(2, '0')}</span>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </nav>

                    <div className='space-y-14 lg:col-span-8 lg:col-start-5'>
                        {sections.map((section, index) => (
                            <section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`} className='scroll-mt-28'>
                                <h2 id={`${section.id}-title`} className='flex items-baseline gap-4 font-heading text-2xl font-medium text-burgundy'>
                                    <span className='font-mono text-xs text-stone'>{String(index + 1).padStart(2, '0')}</span>
                                    {section.title}
                                </h2>

                                <div className='mt-5 space-y-4 leading-relaxed text-stone-dark [&_a]:font-medium [&_a]:text-burgundy [&_a]:underline [&_a]:underline-offset-4 [&_li]:pl-1 [&_strong]:font-medium [&_strong]:text-burgundy [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5'>
                                    {section.body}
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </Container>
        </article>
    )
}
